import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import { Button as MuiButton } from '@mui/material';
import * as Sentry from '@sentry/react';
import Cookies from 'js-cookie';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { io } from 'socket.io-client';
import { twMerge } from 'tailwind-merge';
import tw from 'twin.macro';

import BulbIcon from '../../assets/svg/BulbIcon';
import CallIcon from '../../assets/svg/CallIcon';
import ClockIcon from '../../assets/svg/ClockIcon';
import FileIcon from '../../assets/svg/FileIcon';
import LoaderIcon from '../../assets/svg/LoaderIcon';
import PlayIcon from '../../assets/svg/PlayIcon';
import {
  Appointment,
  AppointmentInformation,
  CallProgress,
  CommonDiscard,
  DialPadCall,
  DialPadPrompts,
  Email2,
  LeadDetails,
  NotInterested,
  PanelEditContact,
  PhoneNumberSelector,
  Pitch,
  Schedule1,
  Schedule2,
  Schedule3,
  ScriptTab,
  WhatHappened,
} from '../../components/CallCenter';
import {
  CallSearchBox,
  Objections,
  Playback,
  PointerStats,
  Practice,
} from '../../components/ConversionLab';
import {
  CallNotesModal,
  DiscardModal,
  EditContactModal,
  FinishCallModal,
  LeadsUnavailable,
  OptimizeScriptWithAIModal,
  ScriptModal,
  SearchModal,
  UnsavedNumberModal,
} from '../../components/Modals';
import {
  Button,
  ButtonCallNotes,
  ButtonGoBack,
  CircularProgress,
  Fetcher,
  Icon,
  IconButton,
} from '../../components/Shared';
import { CALL_STATUS, DATE_TIME_FORMAT, TWILIO_CONFIG } from '../../constants';
import { getTimezoneAbbreviation } from '../../helpers';
import { useTwilioDeviceContext } from '../../hooks';
import { Layout } from '../../layouts';

const socket = io('https://devback.accelerateiq.ai');

const entryDetails = [
  { mode: 'Pitch', icon: <FileIcon />, label: 'Pitch' },
  { mode: 'Pointers', icon: <BulbIcon />, label: 'Pointers' },
  { mode: 'Practice', icon: <ClockIcon />, label: 'Practice' },
  { mode: 'Playback', icon: <PlayIcon />, label: 'Playback' },
];

const StyledButton = styled(MuiButton)(() => [
  tw`bg-transparent hover:bg-base-700 text-destructive-500 font-medium text-[18px]/[24px] normal-case px-[22px] py-3 rounded-full font-sans`,
]);

const INITIAL_PITCH_STATE = {
  visible: false,
  value: {
    isPitchedProduct: null,
    isSolutionPitchCompleted: null,
  },
};

export const CallCenter = () => {
  const {
    call,
    timer,
    callStatus,
    isMicroMute,
    twilioDevice,
    isCallAvailable,
    isDialPadPromptsVisible,
    isTwilioTokenError,
    isTwilioTokenLoading,
    twilioDeviceRegisterStatus,
    setCall,
    setTimer,
    handleMute,
    setCallStatus,
    setIsMicroMute,
    setTwilioDevice,
    handleSendDigits,
    createTwilioDevice,
    setIsDialPadPromptsVisible,
  } = useTwilioDeviceContext();
  const [isDialPadCallVisible, setIsDialPadCallVisible] = useState(false);
  const [dialPadPhoneNumber, setDialPadPhoneNumber] = useState('');
  const [pageText, setPageText] = useState({});
  const [happenedIndex, setHappenedIndex] = useState(1);
  const [index, setIndex] = useState(0);
  const [pickerInfo, setPickerInfo] = useState(moment());
  const [timeInfo, setTimeInfo] = useState();
  const [clicked, setClicked] = useState(false);
  const [loader, setLoader] = useState(false);
  const [openEditContactModal, setOpenEditContactModal] = useState(false);
  const [isSearchModalOpened, setIsSearchModalOpened] = useState(false);
  const [openCallNotesModal, setOpenCallNotesModal] = useState(false);
  const [openDeclineModal, setOpenDeclineModal] = useState(false);
  const [isLeadsAvailable, setIsLeadsAvailable] = useState(true);
  const [isScriptModalOpened, setIsScriptModalOpened] = useState(false);
  const [
    isOptimizeScriptWithAIModalOpened,
    setIsOptimizeScriptWithAIModalOpened,
  ] = useState(false);
  const [isUnsavedNumberModalOpened, setIsUnsavedNumberModalOpened] =
    useState(false);
  const [isFinishCallModalOpened, setIsFinishCallModalOpened] = useState(false);
  const [selected, setSelected] = useState('contactName');
  const [searchResults, setSearchResults] = useState([]);
  const [formObjContact, setFormObjContact] = useState({
    leadid: '',
    company: '',
    contact: '',
    title: '',
    industry: '',
    time: '',
    country: '',
    state: '',
    city: '',
    address: '',
    contactId: '',
    website: '',
    linkedin: '',
    email: '',
    zip: '',
    apptDate: '',
    apptTime: '',
    apptType: '',
    bestPhone: '',
    userId: '',
    phoneNumbers: [],
  });
  const [phoneNumberToCall, setPhoneNumberToCall] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [topBar, setTopBar] = useState({});
  const [chatgptResponse, setChatgptResponse] = useState('');
  const [scriptText, setScriptText] = useState('');
  const [objSelected, setObjSelected] = useState(false);
  const [formObjNotes, setFormObjNotes] = useState({
    callNotes: '',
  });
  const [appointment, setAppointment] = useState('Discovery Call');
  const [isPlayer, setIsPlayer] = useState('hidden');
  const [hangupClicked, setHangupClicked] = useState(false);
  const [formObj, setFormObj] = useState({
    name: '',
    title: '',
    phone: '',
    time: 'EST',
    email: '',
  });
  const [entryMode, setEntryMode] = useState('');
  const [seeTipsClicked, setSeeTipsClicked] = useState(false);
  const [dialOutcomeDetail, setDialOutcomeDetail] = useState('');
  const [practiceCallDialed, setPracticeCallDialed] = useState(false);
  const [subObjectionsList, setSubObjectionsList] = useState([]);
  const [curObjection, setCurObjection] = useState({
    MainObjectionResponse: '',
    MainObjection: '',
  });
  const [curSubObjection, setCurSubObjection] = useState({
    SubObjectionResponse: '',
    SubObjection: '',
  });
  const [subObjSelected, setSubObjSelected] = useState(false);
  const [isScriptBubbleUp, setIsScriptBubbleUp] = useState(false);
  const [isBubbleUp, setIsBubbleUp] = useState(false);
  const [isSubBubbleUp, setIsSubBubbleUp] = useState(false);
  const [isGPTForObj, setIsGPTForObj] = useState(false);
  const [gptObjectionResponse, setGptObjectionResponse] = useState('');
  const [formCallNotes, setFormCallNotes] = useState('');
  const [contactExpanded, setContactExpanded] = useState(false);
  const [triggerCallOptions, setTriggerCallOptions] = useState(0);
  const [triggerCallerScipts, setTriggerCallerScipts] = useState(0);
  const [currentScriptName, setCurrentScriptName] = useState('');
  const [scriptEdit, setScriptEdit] = useState({
    ScriptName: '',
    ScriptDescription: '',
    DefaultScript: false,
  });
  const [scriptSelected, setScriptSelected] = useState(false);
  const [callerScripts, setCallerScripts] = useState([]);
  const [leadLoaded, setLeadLoaded] = useState(false);
  const [isDialDisabled, setIsDialDisabled] = useState(false);
  const [callSID, setCallSID] = useState('');
  const [pitchState, setPitchState] = useState(INITIAL_PITCH_STATE);

  const resetPitchState = (visible = false) => {
    setPitchState({ ...INITIAL_PITCH_STATE, visible });
  };

  const openOptimizeScriptWithAIModal = () =>
    setIsOptimizeScriptWithAIModalOpened(true);
  const closeOptimizeScriptWithAIModal = () =>
    setIsOptimizeScriptWithAIModalOpened(false);

  const handleOpenScriptEditModal = () => setIsScriptModalOpened(true);
  const handleCloseScriptEditModal = () => setIsScriptModalOpened(false);

  const handleOpenUnsavedNumberModal = () =>
    setIsUnsavedNumberModalOpened(true);
  const handleCloseUnsavedNumberModal = () =>
    setIsUnsavedNumberModalOpened(false);

  const handleOpenFinishCallModal = () => setIsFinishCallModalOpened(true);
  const handleCloseFinishCallModal = () => setIsFinishCallModalOpened(false);

  const getUserTimeZone = () => {
    const userTimeZone = moment.tz.guess();
    const userTimeZoneAbbreviation = moment().tz(userTimeZone).format('z');
    return userTimeZoneAbbreviation;
  };

  const handleGetGPTResponse = (draftResponse, objection) => {
    setGptObjectionResponse('');
    var promptForObjection;
    if (!objection) {
      promptForObjection = `I'm a cold caller. Can you provide a more polished and friendly version of the text? Here is the draft: ${draftResponse}
        Please return answers with no extra words like 'Absolutely! Here is a more polished and friendly version...' or ' Here is a refined version ...' or 'Certainly! Here's a revised version...' or 'Certainly! Here you go...'.`;
    } else if (objection === 'Call me back') {
      promptForObjection = `What is a good rebuttal when someone says "call me back" on a cold call where I am trying to schedule a demo of a software solution? Response should be 50 words or more and should be focused on asking the best time to call back and  should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand etc.`;
    } else if (objection === 'How did you get my number?') {
      promptForObjection = `What is a good rebuttal when someone says "How did you get my number?" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words or more and should include 2 optional reasons for why they are not interested and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    } else if (objection === "I can't talk right now, I'm busy") {
      promptForObjection = `What is a good rebuttal when someone says "I' can't talk now, I'm busy" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words or more and should include 2 optional reasons for why they are not interested and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    } else if (objection === "I'm not interested") {
      setGptObjectionResponse(draftResponse);
      return;
    } else if (objection === "I'm not the right person") {
      promptForObjection = `What is a good rebuttal when someone says "I'm not the right person" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    } else if (objection === "I'm walking into a meeting") {
      promptForObjection = `What is a good rebuttal when someone says "I'm walking into a meeting" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words or more and should be focused on asking the best time to call back and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    } else if (objection === 'Is this a cold call?') {
      promptForObjection = `What is a good rebuttal when someone says "Is this a cold call" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words or more and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    } else if (objection === 'Send me an email') {
      promptForObjection = `What is a good rebuttal when someone says "send me an email" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words or more and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    } else if (objection === 'What are you selling?') {
      promptForObjection = `What is a good rebuttal when someone says "what are you selling?" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words or more and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    } else if (objection === "What's this call about?") {
      promptForObjection = `What is a good rebuttal when someone says "what is this call about?" on a cold call where I am trying to schedule a demo of a software solution? Response should only include the rebuttal and it should be 50 words or more and should only include the rebuttal, no extra enclosing words around content, no quotation mark, no extra words like Certainly, Absolutely, I understand, A good rebuttal to, etc.`;
    }

    fetch('/api/v1/chatgpt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: promptForObjection,
      }),
    }).then(response => {
      if (response.status === 200) {
        response.json().then(data => {
          setGptObjectionResponse(data.answer);
        });
      } else if (response.status === 500) {
        fetch('/api/v1/chatgpt', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: promptForObjection,
          }),
        }).then(resp => {
          if (resp.status === 200) {
            resp.json().then(respData => {
              setGptObjectionResponse(respData.answer);
            });
          }
        });
      }
    });
  };

  const handleObjectionSelect = objectionSeleted => {
    setCurObjection(objectionSeleted);
    setObjSelected(true); //setSubObjectionsList
    setSubObjectionsList([]);
    fetch('/api/v1/pitch/sub-objection', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        objection: objectionSeleted.MainObjection,
      }),
    }).then(r => {
      if (r.ok) {
        r.json().then(data => {
          setSubObjectionsList(data.subobjections);
        });
      } else {
        setSubObjectionsList([]);
      }
    });
  };

  const handleSubObjectionSelect = item => {
    setCurSubObjection(item);
    setSubObjSelected(true);
  };

  const handleFinish = () => {
    fetch('/find-lead', {
      method: 'GET',
    })
      .then(res => {
        if (res.status !== 200) throw new Error();
        setPickerInfo(moment());
        setTimeInfo();
        setHappenedIndex(1);
        setIndex(0);
        setDialPadPhoneNumber('');
        setTriggerCallOptions(triggerCallOptions + 1);
        setDialOutcomeDetail('');
        resetPitchState();
        setIsDialOutcomesLoading(false);
        setOpenDeclineModal(false);
        setAppointment('Discovery Call');
      })
      .catch(() => {
        setIsDialOutcomesLoading(false);
      });
  };

  const [isDialOutcomesLoading, setIsDialOutcomesLoading] = useState(false);
  const [isDialOutcomesError, setIsDialOutcomesError] = useState(false);
  const updateDialOutcomes = (
    nextHappenedIndex = happenedIndex,
    dialOutcome = '',
  ) => {
    setIsDialOutcomesLoading(true);
    setIsDialOutcomesError(false);
    let choice = '';
    let notInterestedChoice = '';
    let doNotCallChoice = '';
    let detail = '';
    let scheduleType = 0;
    let AppointmentDateTime = '';
    let AppointmentType = '';
    let CallerTimeZone = getTimezoneAbbreviation();
    if (nextHappenedIndex === 0) {
      choice = 'FirstAppt';
      detail = 'N/A';
      scheduleType = 2;
      AppointmentDateTime =
        pickerInfo.format(DATE_TIME_FORMAT.YYYY_MM_DD_DASH) + ' ' + timeInfo;
      AppointmentType = appointment;
    } else if (nextHappenedIndex === 1) {
      choice = 'Callback';
      detail = 'N/A';
      scheduleType = 1;
      AppointmentDateTime =
        pickerInfo.format(DATE_TIME_FORMAT.YYYY_MM_DD_DASH) + ' ' + timeInfo;
    } else if (nextHappenedIndex === 2) {
      choice = 'EmailSent';
      detail = 'N/A';
    } else if (nextHappenedIndex === 3) {
      choice = 'NewContact';
      detail = 'N/A';
    } else if (nextHappenedIndex === 4) {
      choice = 'HangUp';
      detail = 'N/A';
    } else if (nextHappenedIndex === 5) {
      choice = 'NotInterested';
      detail = 'N/A';
      notInterestedChoice = dialOutcomeDetail;
    } else if (nextHappenedIndex === 6) {
      choice = 'NoAns';
      detail = 'N/A';
    } else if (nextHappenedIndex === 7) {
      choice = 'Discarded';
      detail = dialOutcome;
    } else if (nextHappenedIndex === 8) {
      choice = 'DoNotCall';
      detail = dialOutcome;
      doNotCallChoice = dialOutcomeDetail;
    }

    fetch('/dialoutcomes', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dialOutcome: choice,
        detail: detail,
        scheduleType: scheduleType,
        AppointmentDate: AppointmentDateTime,
        AppointmentType: AppointmentType,
        CallerTimeZone: CallerTimeZone,
        callSid: callSID,
        connectedPhoneNumber:
          dialPadPhoneNumber || phoneNumberToCall.PhoneNumber,
        isPitchedProduct: pitchState.value.isPitchedProduct,
        ...(pitchState.value.isSolutionPitchCompleted === 1 && {
          isSolutionPitchCompleted: pitchState.value.isSolutionPitchCompleted,
        }),
        ...(!!notInterestedChoice && { notInterestedChoice }),
        ...(!!doNotCallChoice && { doNotCallChoice }),
      }),
    })
      .then(response => {
        if (response.status !== 200) throw new Error();
        handleFinish();
        response.json().then(() => {
          Cookies.remove('ID', { path: '' });
        });
      })
      .catch(() => {
        setIsDialOutcomesError(true);
        setIsDialOutcomesLoading(false);
        toast.error('We couldn’t save call results.', {
          position: 'top-right',
          autoClose: 3000,
        });
      });
  };

  const handleOpenEditContactModal = () => setOpenEditContactModal(true);
  const handleCloseEditContactModal = () => setOpenEditContactModal(false);

  const handleOpenSearchModal = () => setIsSearchModalOpened(true);
  const handleCloseSearchModal = () => setIsSearchModalOpened(false);

  const handleOpenCallNotesModal = () => setOpenCallNotesModal(true);
  const handleCloseCallNotesModal = () => setOpenCallNotesModal(false);

  const toggleIsPlayer = type => {
    if (type === 'hidden') {
      setIsPlayer('hidden');
    } else {
      setIsPlayer('');
    }
  };

  useEffect(() => {
    socket.on('message', msg => {
      console.log('Message from server', msg);
    });

    return () => {
      socket.off('message');
    };
  }, []);

  const handleDialClick = () => {
    if (!isDialDisabled) {
      setIsDialDisabled(true);
      setLoader(true);
      setTimeout(() => {
        if (clicked) {
          call.disconnect();
        } else {
          setClicked(true);
          initiateCall();
        }
      }, 1000);
    }
  };

  const hasPhoneNumberToSave =
    dialPadPhoneNumber.length === 10 &&
    !formObjContact.phoneNumbers.find(
      ({ PhoneNumber }) => PhoneNumber === dialPadPhoneNumber,
    );

  const initiateCall = () => {
    const CallerPhone = Cookies.get('CallerPhone');
    const phoneNumber = isDialPadCallVisible
      ? dialPadPhoneNumber
      : phoneNumberToCall.PhoneNumber;

    const params = {
      To: '+1' + phoneNumber,
      From: CallerPhone,
      CallerPhone: CallerPhone,
      LeadId: formObjContact.leadid,
      CallerId: formObjContact.userId,
    };
    twilioDevice
      .connect({
        params,
        rtcConstraints: {
          audio: true,
        },
      })
      .then(connection => {
        setCall(connection);
        setLoader(false);
        setIsDialDisabled(false);
        setIsDialPadCallVisible(false);

        connection.on('mute', isMuted => {
          setIsMicroMute(isMuted);
        });

        connection.on('accept', () => {
          setCallStatus(CALL_STATUS.ACCEPT);
        });

        connection.addListener('disconnect', conn => {
          setCallSID(conn.parameters.CallSid);
          setHangupClicked(true);
          setIndex(1);
          setIsDialPadPromptsVisible(false);
          setIsMicroMute(false);
          setCall(null);
          setCallStatus(CALL_STATUS.RINGING);
          setTimer(0);
          setLoader(false);
          setClicked(false);
          setIsDialDisabled(false);
          if (!hasPhoneNumberToSave) {
            setDialPadPhoneNumber('');
          }
        });
      })
      .catch(error => {
        Sentry.captureException(new Error(error));
        twilioDevice.disconnectAll();
        setLoader(false);
        setIsDialDisabled(false);
        setClicked(false);
        if (!error) return;
        toast.info(error?.message ?? TWILIO_CONFIG.DEFAULT_ERROR_MESSAGE, {
          position: 'top-right',
          autoClose: 3000,
        });
      });
  };

  const initiatePracticeCall = phone => {
    const CallerPhone = Cookies.get('CallerPhone');
    const params = {
      To: phone,
      From: CallerPhone,
      CallerPhone: CallerPhone,
    };
    twilioDevice
      .connect({
        params,
        rtcConstraints: {
          audio: true,
        },
      })
      .catch(error => {
        Sentry.captureException(new Error(error));
        if (!error) return;
        toast.info(error?.message ?? TWILIO_CONFIG.DEFAULT_ERROR_MESSAGE, {
          position: 'top-right',
          autoClose: 3000,
        });
      });
  };

  const handlePracticeCallHangup = () => {
    setPracticeCallDialed(false);
    twilioDevice.disconnectAll();
  };

  function formatDateToCustomString(dateString) {
    if (dateString) {
      const originalDate = new Date(dateString);
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Intl.DateTimeFormat('en-US', options).format(originalDate);
    } else {
      return '';
    }
  }

  const handleNotInterested = () => {
    setObjSelected(false);
    setSubObjSelected(false);
  };

  function displayFormattedDate(elementId) {
    const dateElement = document.getElementById(elementId);
    if (dateElement) {
      const currentDate = new Date();
      const options = { weekday: 'long', month: 'long', day: 'numeric' };
      const formattedDate = currentDate.toLocaleDateString('en-US', options);
      dateElement.innerHTML = formattedDate;
    }
  }

  function displayFormattedTime(elementId) {
    const timeElement = document.getElementById(elementId);
    if (timeElement) {
      const currentTime = new Date();
      const formattedTime = currentTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      });
      timeElement.innerHTML = formattedTime;
    }
  }

  const [initialPrompt, setInitialPrompt] = useState('');

  useEffect(() => {
    document.title = 'AccelerateIQ - Start Calling';
    const yourFunction = () => {
      displayFormattedDate('currentDate');
      displayFormattedTime('currentTime');
    };
    const intervalId = setInterval(yourFunction, 60000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    displayFormattedDate('currentDate');
    displayFormattedTime('currentTime');

    fetch('/api/script/list-scripts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    }).then(response => {
      if (response.status === 200) {
        response.json().then(data => {
          setCallerScripts(data.scripts);
          if (!scriptSelected) {
            setTimeout(() => setIsScriptBubbleUp(true), 500);
          }
        });
      } else if (response.status === 404) {
        setCallerScripts([]);
        setScriptSelected(false);
        setTimeout(() => setIsScriptBubbleUp(true), 500);
      }
    });
  }, [triggerCallerScipts]);

  useEffect(() => {
    if (!initialPrompt) {
      return;
    }
    setChatgptResponse('');
    const promptText = `${initialPrompt}. Here is my current script: ${scriptEdit.ScriptDescription}`;

    fetch('/api/v1/chatgpt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: promptText,
      }),
    }).then(response => {
      if (response.status === 200) {
        response.json().then(data => {
          setChatgptResponse(data.answer);
        });
      } else if (response.status === 500) {
        fetch('/api/v1/chatgpt', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message: promptText,
          }),
        }).then(resp => {
          if (resp.status === 200) {
            resp.json().then(respData => {
              setChatgptResponse(respData.answer);
            });
          }
        });
      }
    });
  }, [scriptEdit, initialPrompt]);

  const PagerSetter = async data => {
    if (!data || !data['callerDetails'] || !data['callerDetails'].length) {
      console.error('Error: Unable to access callerDetails from data.');
      return;
    }
    var url = data['callOptions'][0].Website;

    if (url === null || url === undefined || url === '') {
      url = '';
    } else if (!url.includes('https://') && !url.includes('http://')) {
      url = `${url}`;
    }

    setFormObjContact({
      leadid: data['callOptions'][0].LeadId,
      company: data['callOptions'][0].CompanyName,
      contact: data['callOptions'][0].ContactName,
      title: data['callOptions'][0].Title,
      industry: data['callOptions'][0].Industry,
      time: data['callOptions'][0].TargetIanaTimeZone,
      country: data['callOptions'][0].Country,
      state: data['callOptions'][0].State,
      city: data['callOptions'][0].City,
      address: data['callOptions'][0].Address_1,
      contactId: data['callOptions'][0].ContactId,
      website: url,
      linkedin: data['callOptions'][0].LinkedInProfile,
      email: data['callOptions'][0].Email,
      zip: data['callOptions'][0].ZipCode,
      apptDate: pickerInfo.format(DATE_TIME_FORMAT.MMMM_Do_YYYY),
      apptTime: `${timeInfo} EST`,
      apptType: 'First Appointment - 30 Minutes',
      bestPhone: data['callOptions'][0].BestPhoneNumber,
      userId: data['callerDetails'][0].UserId,
      phoneNumbers: data['callOptions'][0].PhoneNumbers,
    });
    const initialPhoneNumberToCall = data['callOptions'][0].PhoneNumbers.find(
      ({ IsBestPhoneNumber }) => IsBestPhoneNumber,
    );
    setPhoneNumberToCall(initialPhoneNumberToCall);
    setTopBar({
      campaign: data['callOptions'][0].CampaignShortName,
      callCategory: data['callOptions'][0].CallCategory,
      leadId: data['callOptions'][0].LeadId,
      callDate: data['callOptions'][0].LastDialed
        ? moment(data['callOptions'][0].LastDialed).format(
            DATE_TIME_FORMAT.MMMM_DD_YYYY,
          )
        : '',
    });
    setFormObjNotes({
      callNotes: data['callOptions'][0].CallerNotes ?? '',
      leadId: data['callOptions'][0].LeadId,
      tenantId: data['callOptions'][0].TenantID,
    });
    setFormCallNotes(data['callOptions'][0].CallerNotes ?? '');
    setPageText({
      script: data['callOptions'][0].Script,
      offer: data['callOptions'][0].Offer,
      voicemail: data['callOptions'][0].VoiceMail,
    });
    setScriptText(data['callOptions'][0].Script);

    const contactNameArray = data['callOptions'][0].ContactName?.split(' ');
    const firstName = contactNameArray?.[0];
    let companyDetailLinkedIn = '',
      companyDetailGoogle = '';
    const linkedin_url = data['callOptions'][0].LinkedInProfile;

    if (!!linkedin_url && linkedin_url.includes('company')) {
      try {
        const linkeninRidResp = await fetch('/api/v1/linkedin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ linkedin_url }),
        });

        if (linkeninRidResp.ok) {
          const linkedin_rid = await linkeninRidResp.json();

          try {
            const linkedinDataResp = await fetch('/api/v1/storage', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                rid: linkedin_rid.rid,
              }),
            });
            if (linkedinDataResp.ok) {
              const linkedinData = await linkedinDataResp.json();
              companyDetailLinkedIn = linkedinData.description;
            }
          } catch (error) {
            companyDetailLinkedIn = '';
          }
          companyDetailLinkedIn = '';
        }
      } catch (error) {
        companyDetailLinkedIn = '';
      }
    }

    try {
      const googleDataResp = await fetch('/api/v1/google', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          company: data['callerDetails'][0].CompanyName,
        }),
      });
      if (googleDataResp.ok) {
        const googleData = await googleDataResp.json();
        companyDetailGoogle =
          googleData.body.searchResults?.[0]?.description ?? '';
      }
    } catch (error) {
      companyDetailGoogle = '';
    }

    setInitialPrompt(
      `I have a phone call with ${data['callOptions'][0].ContactName} and here is the company's linkedin profile ${companyDetailLinkedIn}. and this is google search result: ${companyDetailGoogle}. I am going to try to convince ${firstName} to schedule a sales appointment with me and I want the first paragraph of my pitch to include something that might be of interest to ${firstName} based on his LinkedIn profile. I also want the second paragraph of my script to reference a recent news item about ${data['callOptions'][0].CompanyName}. Please search the internet for the most recent news story about ${data['callOptions'][0].CompanyName} and insert something about the news story in the second paragraph of my script. Include a reference to one of ${data['callOptions'][0].CompanyName} products in the last paragraph of my script and the benefit ${firstName} will get from working with ${data['callerDetails'][0].CompanyName} in relation to that ${data['callOptions'][0].CompanyName} product. Please adapt my current script to include these items. Please return answers not extra words like 'here is an adapted script for...' or 'Feel free to modify the script...'. `,
    );

    const promptText = `I have a phone call with ${data['callOptions'][0].ContactName} and here is the company's linkedin profile ${companyDetailLinkedIn}. and this is google search result: ${companyDetailGoogle}. I am going to try to convince ${firstName} to schedule a sales appointment with me and I want the first paragraph of my pitch to include something that might be of interest to ${firstName} based on his LinkedIn profile. I also want the second paragraph of my script to reference a recent news item about ${data['callOptions'][0].CompanyName}. Please search the internet for the most recent news story about ${data['callOptions'][0].CompanyName} and insert something about the news story in the second paragraph of my script. Include a reference to one of ${data['callOptions'][0].CompanyName} products in the last paragraph of my script and the benefit ${firstName} will get from working with ${data['callerDetails'][0].CompanyName} in relation to that ${data['callOptions'][0].CompanyName} product. Please adapt my current script to include these items. Please return answers not extra words like 'here is an adapted script for...' or 'Feel free to modify the script...'. Here is my current script: ${scriptEdit.ScriptDescription}`;

    fetch('/api/v1/chatgpt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: promptText,
      }),
    }).then(response => {
      if (response.status === 200) {
        response.json().then(data => {
          setChatgptResponse(data.answer);
        });
      }
    });
  };

  const [isCallOptionsLoading, setIsCallOptionsLoading] = useState(true);
  const [isCallOptionsError, setIsCallOptionsError] = useState(false);

  useEffect(() => {
    const fetchCallOptions = async () => {
      setIsCallOptionsLoading(true);
      setIsCallOptionsError(false);
      setIsLeadsAvailable(true);

      try {
        const response = await fetch('/call-options', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            CallerTimeZone: getTimezoneAbbreviation(),
          }),
        });

        if (response.status === 404) {
          setLeadLoaded(false);
          setIsLeadsAvailable(false);
          setIsCallOptionsLoading(false);
          setIsCallOptionsError(false);
          return;
        }

        if (response.status !== 200) throw new Error();

        const data = await response.json();

        PagerSetter(data);
        setIndex(0);
        setClicked(false);
        setLeadLoaded(true);
        setIsCallOptionsLoading(false);
      } catch {
        setLeadLoaded(false);
        setIsCallOptionsLoading(false);
        setIsCallOptionsError(true);
      }
    };

    fetchCallOptions();
  }, [triggerCallOptions]);

  const handleClickEntryIcon = entry => {
    setEntryMode(entry);
    setTabIndex(1);
    setSeeTipsClicked(false);
  };

  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      setTimeout(() => {
        if (clicked) {
          handleDialClick();
        }
      }, 1000);
    };
  }, []);

  const emptyEntryModeArray = [
    {
      label: 'Playback',
      icon: <PlayIcon className="w-full h-full" />,
      onClick: () => handleClickEntryIcon('Playback'),
    },
    {
      label: 'Pitch',
      icon: <FileIcon className="w-full h-full" />,
      onClick: () => {
        handleClickEntryIcon('Pitch');
        setTimeout(() => setIsScriptBubbleUp(true), 500);
      },
    },
    {
      label: 'Pointers',
      icon: <BulbIcon className="w-full h-full" />,
      onClick: () => handleClickEntryIcon('Pointers'),
    },
    {
      label: 'Practice',
      icon: <ClockIcon className="w-full h-full" />,
      onClick: () => handleClickEntryIcon('Practice'),
    },
  ];

  const isPanelEditContactVisible = !(
    (index === 5 && happenedIndex === 0) ||
    index === 0
  );
  const dialButtonTitleByCallStatus = clicked ? 'End' : 'Dial';
  const dialButtonTitle = loader ? '' : dialButtonTitleByCallStatus;
  const isCallNotAvailable =
    !(isCallAvailable && leadLoaded && !isDialDisabled) ||
    (isDialPadCallVisible && dialPadPhoneNumber.length < 10);

  const handleFinishCall = () => {
    if (hasPhoneNumberToSave) {
      handleOpenFinishCallModal();
    } else {
      updateDialOutcomes();
    }
  };

  return (
    <Layout>
      {index === 5 && happenedIndex === 0 && (
        <div className={`pointer-events-none fixed inset-0 z-20 ${isPlayer}`}>
          <Player
            src={
              'https://lottie.host/43ca7dd8-6b2c-4fae-b8cb-7b9a6e25c4e8/uqvEeg4Nc5.json'
            }
            autoplay
            loop
          />
        </div>
      )}{' '}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="">
          <div className="flex flex-wrap gap-x-10 gap-y-1">
            <h2
              id="currentDate"
              className="text-2xl font-medium lg:text-3xl 2xl:text-4xl"
            >
              Wednesday, September 25
            </h2>
            <h2
              id="currentTime"
              className="text-2xl font-medium lg:text-3xl 2xl:text-4xl"
            >
              9:05 AM
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-4"></div>
      </div>
      <div className="mt-2 h-mt-6 grid flex-1 grid-cols-1 gap-6 xl:grid-cols-2 xl:gap-10 ">
        <div className="flex flex-col relative rounded-2xl bg-secondary/10 p-3 2xl:p-10">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b-[3px] border-secondary/20 pb-2">
            <div className="relative flex items-center gap-2 before:absolute before:-bottom-[11px] before:left-0 before:h-[3px] before:w-[calc(100%+.5rem)] before:bg-secondary">
              <span
                className="rounded-lg bg-accent-1 p-1 text-primary cursor-pointer transition-transform duration-300 ease-in-out hover:scale-125"
                onClick={() => setEntryMode('')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  className="w-6 h-6"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
                  />
                </svg>
              </span>
              <h6 className="text-lg font-medium">Conversion Lab</h6>
            </div>
            {entryDetails.map(
              ({ mode, icon, label }) =>
                entryMode === mode && (
                  <div className="flex items-center" key={mode}>
                    <span className="inline-block w-8 h-8 items-center justify-center">
                      {icon}
                    </span>
                    <span className="ml-2 text-accent-2">{label}</span>
                  </div>
                ),
            )}
          </div>
          <div
            className="mt-10 mx-4 mb-6 relative h-full min-h-[360px] overflow-hidden rounded-2xl bg-white/50 p-6 2xl:p-10"
            id="scroll"
          >
            <div className="mt-0 flex h-full flex-col gap-4">
              {entryMode === '' && (
                <div className="items-center h-full flex flex-col justify-center">
                  <div className="grid grid-cols-2 gap-20 2xl:gap-20 text-center">
                    {emptyEntryModeArray.map(({ label, icon, onClick }) => (
                      <div
                        key={label}
                        className="flex flex-col cursor-pointer items-center justify-center group"
                        onClick={onClick}
                      >
                        <span className="relative transition-transform duration-300 ease-in-out hover:scale-125 hover:opacity-50 inline-flex w-24 h-24 2xl:w-28 2xl:h-28 flex items-center justify-center">
                          {icon}
                          <span className="absolute inset-0 flex items-center justify-center text-white text-xl font-bold opacity-0 group-hover:opacity-100">
                            {label}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {entryMode === 'Pitch' && (
                <>
                  <div className="border-b border-gray-200">
                    <nav
                      className="flex space-x-4 lg:space-x-8"
                      aria-label="Tabs"
                      role="tablist"
                    >
                      <button
                        type="button"
                        className={`${
                          tabIndex !== 3 && 'active'
                        } flex-1 inline-flex justify-center items-center gap-x-2 whitespace-nowrap border-b-2 border-transparent px-1 py-4 font-medium text-primary/40 transition hover:text-primary focus:text-secondary focus:outline-none disabled:pointer-events-none disabled:opacity-50 hs-tab-active:border-secondary hs-tab-active:text-secondary`}
                        id="todays-stats-tab-btn"
                        data-hs-tab="#todays-stats-tab"
                        aria-controls="todays-stats-tab"
                        onClick={() => {
                          setTabIndex(1);
                          setTimeout(() => setIsScriptBubbleUp(true), 500);
                          setIsBubbleUp(false);
                        }}
                        role="tab"
                      >
                        Script
                      </button>
                      <button
                        type="button"
                        className={`${
                          tabIndex === 3 && 'active'
                        } flex-1 inline-flex justify-center items-center gap-x-2 whitespace-nowrap border-b-2 border-transparent px-1 py-4 font-medium text-primary/40 transition hover:text-primary focus:text-secondary focus:outline-none disabled:pointer-events-none disabled:opacity-50 hs-tab-active:border-secondary hs-tab-active:text-secondary`}
                        id="this-month-tab-btn"
                        data-hs-tab="#this-month-tab"
                        aria-controls="this-month-tab"
                        onClick={() => {
                          setTabIndex(3);
                          setIsScriptBubbleUp(false);
                          setTimeout(() => setIsBubbleUp(true), 500);
                        }}
                        role="tab"
                      >
                        Objections
                      </button>
                    </nav>
                  </div>
                  <div
                    className={`${
                      tabIndex !== 3 ? 'block' : 'hidden'
                    } max-h-[340px] ${
                      isScriptBubbleUp || tabIndex === 2 || scriptSelected
                        ? 'overflow-y-auto'
                        : 'overflow-y-none'
                    } ${
                      callerScripts.length
                        ? ''
                        : 'items-center justify-center flex h-full'
                    } [&::-webkit-scrollbar-thumb]:bg-secondary/80 [&::-webkit-scrollbar-track]:bg-secondary/20 [&::-webkit-scrollbar]:w-2`}
                  >
                    {tabIndex === 1 &&
                      !scriptSelected &&
                      callerScripts.map((item, index) => (
                        <div
                          key={index}
                          className={`flex min-w-24 items-center justify-between gap-x-2 rounded-full bg-secondary/10 p-2 text-center text-sm 2xl:text-base font-medium leading-none text-accent-2 transition-colors focus:outline-none focus:ring focus:ring-primary/20 disabled:pointer-events-none disabled:opacity-50 md:min-w-32 2xl:text-base mr-2 mb-5 cursor-pointer transition-all duration-1000 ${
                            isScriptBubbleUp
                              ? 'translate-y-0 opacity-100'
                              : 'translate-y-full opacity-0'
                          }`}
                          style={{
                            transition: `transform ${1.5 - index * 0.1}s ${
                              index * 0.2
                            }s ease-out, opacity ${1.5 - index * 0.1}s ${
                              index * 0.2
                            }s ease-in-out`,
                          }}
                        >
                          <span className="flex">{item.ScriptName}</span>
                          <span
                            className="flex cursor-pointer items-center justify-center gap-x-2 rounded-full border border-accent-2 bg-accent-2 px-2 py-1 text-center font-medium text-white transition hover:border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring focus:ring-primary/20 disabled:pointer-events-none disabled:opacity-50 md:px-4 md:text-lg"
                            onClick={() => {
                              setTimeout(
                                () => openOptimizeScriptWithAIModal(),
                                2800,
                              );
                              setIsScriptBubbleUp(false);
                              setScriptSelected(true);
                              setScriptEdit(item);
                            }}
                          >
                            Select
                          </span>
                        </div>
                      ))}
                    {tabIndex === 1 &&
                      !scriptSelected &&
                      !callerScripts.length && (
                        <div
                          className={`flex w-48 items-center justify-center gap-x-2 rounded-full border border-accent-2 bg-transparent p-3 text-center text-lg 2xl:text-xl font-medium leading-none text-accent-2 transition-colors hover:border-accent-2 hover:bg-accent-2 hover:text-white focus:outline-none focus:ring focus:ring-primary/20 disabled:pointer-events-none disabled:opacity-50 mr-2 mb-5 cursor-pointer transition-all duration-1000 ${
                            isScriptBubbleUp
                              ? 'translate-y-0 opacity-100'
                              : 'translate-y-full opacity-0'
                          }`}
                          onClick={() => {
                            if (!scriptSelected) {
                              setScriptEdit({
                                ScriptName: '',
                                ScriptDescription: '',
                                DefaultScript: 0,
                              });
                            }
                            setIsScriptBubbleUp(false);
                            handleOpenScriptEditModal();
                          }}
                          style={{
                            transition: `transform ${1.5 - index * 0.1}s ${
                              index * 0.2
                            }s ease-out, opacity ${1.5 - index * 0.1}s ${
                              index * 0.2
                            }s ease-in-out`,
                          }}
                        >
                          <span className="text-center">
                            Add your first script
                          </span>
                        </div>
                      )}
                    {tabIndex === 1 && scriptSelected && (
                      <div>
                        <p className="whitespace-preline text-base font-medium lg:text-lg">
                          {scriptEdit.ScriptDescription}
                        </p>
                      </div>
                    )}
                    <div className={`${tabIndex === 2 ? 'block' : 'hidden'}`}>
                      {tabIndex === 2 && chatgptResponse && (
                        <ScriptTab words={chatgptResponse} />
                      )}
                    </div>
                    <button
                      type="button"
                      className={`${
                        tabIndex === 1 && callerScripts.length
                          ? 'block'
                          : 'hidden'
                      } absolute bottom-3 right-3 flex-1 inline-flex justify-center items-center gap-x-2 whitespace-nowrap border-b-2 border-transparent px-1 py-4 font-medium text-secondary transition hover:text-primary focus:text-secondary focus:outline-none z-10`}
                      onClick={() => {
                        if (!scriptSelected) {
                          setScriptEdit({
                            ScriptName: '',
                            ScriptDescription: '',
                            DefaultScript: 0,
                          });
                        }
                        handleOpenScriptEditModal();
                      }}
                      id="edit-script-btn"
                    >
                      {scriptSelected ? 'Edit Script' : 'Add Script'}
                    </button>
                  </div>
                  <div
                    className={`${tabIndex === 3 ? 'block' : 'hidden'} ${
                      objSelected &&
                      !subObjSelected &&
                      subObjectionsList.length > 0
                        ? 'h-full flex flex-col jusstify-around'
                        : ''
                    }`}
                  >
                    {objSelected && gptObjectionResponse && (
                      <ScriptTab words={gptObjectionResponse} />
                    )}
                    {objSelected &&
                      !subObjSelected &&
                      subObjectionsList.length > 0 &&
                      subObjectionsList[0].SubObjection !== null && (
                        <div className="justify-center items-center h-full flex flex-row text-center mt-5">
                          {subObjectionsList.map((item, index) => (
                            <div
                              key={index}
                              className={`cursor-pointer flex flex-col inline-flex min-w-24 items-center justify-center gap-x-2 rounded-full border border-accent-2 bg-transparent px-2 py-1 text-center text-sm 2xl:text-base font-medium leading-none text-accent-2 transition-colors hover:border-accent-2 hover:bg-accent-2 hover:text-white focus:outline-none focus:ring focus:ring-primary/20 disabled:pointer-events-none disabled:opacity-50 md:min-w-32 md:px-4 md:py-2 2xl:text-base mr-2 mb-5 transition-all duration-1000 ${
                                isSubBubbleUp
                                  ? 'translate-y-0 opacity-100'
                                  : 'translate-y-full opacity-0'
                              }`}
                              style={{
                                transition: `transform ${3 - index * 0.1}s ${
                                  index * 0.2
                                }s ease-out, opacity ${3 - index * 0.1}s ${
                                  index * 0.2
                                }s ease-in-out`,
                              }}
                              onClick={() => {
                                setIsGPTForObj(false);
                                handleSubObjectionSelect(item);
                                handleGetGPTResponse(
                                  item.SubObjectionResponse,
                                  '',
                                );
                              }}
                            >
                              {item.SubObjection}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                  {tabIndex === 3 && !objSelected && (
                    <Objections
                      setIsGPTForObj={setIsGPTForObj}
                      handleObjectionSelect={handleObjectionSelect}
                      handleGetGPTResponse={handleGetGPTResponse}
                      setIsSubBubbleUp={setIsSubBubbleUp}
                    />
                  )}
                </>
              )}
              {entryMode === 'Pointers' && <PointerStats />}
              {entryMode === 'Practice' && (
                <Practice
                  initiatePracticeCall={initiatePracticeCall}
                  setPracticeCallDialed={setPracticeCallDialed}
                  practiceCallDialed={practiceCallDialed}
                />
              )}
              {entryMode === 'Playback' && <Playback />}
            </div>
          </div>
          <div className="grid grid-cols-4 flex flex-col items-center 2xl:h-12 xl:h-9 conv-lab-bottom-bar">
            <ButtonGoBack
              onClick={() => {
                if (entryMode === 'Pitch' && objSelected) {
                  setSubObjSelected(false);
                  setObjSelected(false);
                  setIsGPTForObj(false);
                  setIsSubBubbleUp(false);
                } else if (tabIndex === 1 && scriptSelected) {
                  setScriptSelected(false);
                  setTimeout(() => setIsScriptBubbleUp(true), 500);
                } else if (tabIndex === 2) {
                  setTabIndex(1);
                } else {
                  setEntryMode('');
                  setIsScriptBubbleUp(false);
                }
              }}
              className={entryMode === '' ? 'invisible' : undefined}
            />
            {entryMode === 'Pitch' && (
              <span
                className={`${
                  tabIndex === 3 && objSelected ? '' : 'invisible'
                } col-span-2 justify-center items-center cursor-pointer text-center text-lg font-bold text-accent-2`}
                onClick={() => handleNotInterested()}
              >
                {curObjection.MainObjection}
              </span>
            )}
            {entryMode === 'Practice' && (
              <>
                <span
                  className={`col-span-2 flex cursor-pointer items-center justify-center rounded-full ${
                    practiceCallDialed ? '' : 'hidden'
                  }`}
                  style={{ backgroundColor: 'rgb(222, 234, 245)' }}
                  onClick={() => {
                    handlePracticeCallHangup();
                  }}
                >
                  <img
                    src="/hangup.png"
                    alt="img-practice-hangup"
                    className="rounded-full h-10 w-10"
                  />
                </span>
              </>
            )}
            {entryMode === 'Playback' && (
              <div
                className={`${
                  entryMode === 'Playback' ? 'block' : 'hidden'
                } col-span-3 flex-1 inline-flex justify-end items-center border-transparent px-1 py-0 font-medium`}
                id="call-recording-search-btn"
              >
                <CallSearchBox />
              </div>
            )}
          </div>
        </div>
        <div
          className={`${
            index === 0
              ? 'flex flex-col rounded-2xl bg-secondary/10 p-3 2xl:p-10'
              : 'hidden'
          }`}
        >
          <Fetcher
            loading={
              isCallOptionsLoading ||
              isTwilioTokenLoading ||
              twilioDeviceRegisterStatus === 'registering'
            }
            loader={
              <div className="flex justify-center items-center rounded-[20px] bg-white/65 min-h-[574px] flex-1">
                <CircularProgress size={80} />
              </div>
            }
            error={
              isCallOptionsError ||
              isTwilioTokenError ||
              twilioDeviceRegisterStatus === 'unregistered'
            }
            errorBoundary={
              <div className="flex justify-center items-center flex-col gap-3 rounded-[20px] bg-white/65 min-h-[574px] flex-1">
                <div className="flex justify-center items-center flex-col gap-1">
                  <span className="text-base-1000 font-semibold text-[24px]/[32px]">
                    Couldn’t load call center
                  </span>
                  <span className="text-base-800 font-normal text-[16px]/[24px]">
                    Error occurred while loading. Try again.
                  </span>
                </div>
                <StyledButton
                  disableFocusRipple
                  disableRipple
                  variant="text"
                  startIcon={
                    <Icon name="reload" alt="reload" className="h-5" />
                  }
                  onClick={() => {
                    if (isCallOptionsError) {
                      setTriggerCallOptions(prev => prev + 1);
                    }

                    if (
                      isTwilioTokenError ||
                      twilioDeviceRegisterStatus === 'unregistered'
                    ) {
                      !twilioDevice
                        ? createTwilioDevice()
                        : setTwilioDevice(null);
                    }
                  }}
                >
                  Reload
                </StyledButton>
              </div>
            }
            render={() =>
              isLeadsAvailable ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b-[3px] border-secondary/20 pb-2">
                    <div className="relative flex items-center gap-2 before:absolute before:-bottom-[13px] before:left-0 before:h-[3px] before:w-[calc(100%+.5rem)] before:bg-secondary">
                      <CallIcon className="w-5 h-5" />
                      <h6 className="text-lg font-medium">Call Center</h6>
                    </div>
                    {!call && (
                      <button
                        onClick={handleOpenSearchModal}
                        data-hs-overlay="#search-modal"
                        className="inline-flex h-7 w-7 items-center justify-center p-1 text-accent-2"
                      >
                        <svg
                          viewBox="0 0 33 34"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M32.2665 30.895L24.4939 23.124C26.7468 20.4193 27.8701 16.9503 27.6304 13.4385C27.3906 9.92665 25.8062 6.64247 23.2066 4.26911C20.6071 1.89575 17.1927 0.615941 13.6736 0.695918C10.1545 0.775894 6.80177 2.2095 4.31276 4.6985C1.82376 7.18751 0.390152 10.5403 0.310175 14.0594C0.230199 17.5784 1.51001 20.9929 3.88337 23.5924C6.25673 26.1919 9.54091 27.7763 13.0527 28.0161C16.5645 28.2559 20.0336 27.1325 22.7382 24.8797L30.5093 32.6523C30.6247 32.7677 30.7617 32.8592 30.9124 32.9216C31.0632 32.9841 31.2247 33.0162 31.3879 33.0162C31.5511 33.0162 31.7127 32.9841 31.8634 32.9216C32.0142 32.8592 32.1512 32.7677 32.2665 32.6523C32.3819 32.5369 32.4735 32.3999 32.5359 32.2492C32.5983 32.0984 32.6305 31.9368 32.6305 31.7737C32.6305 31.6105 32.5983 31.4489 32.5359 31.2982C32.4735 31.1474 32.3819 31.0104 32.2665 30.895ZM2.82471 14.3874C2.82471 12.1768 3.48022 10.0158 4.70836 8.1778C5.93649 6.33977 7.68209 4.9072 9.7244 4.06124C11.7667 3.21529 14.014 2.99395 16.1821 3.42521C18.3502 3.85648 20.3418 4.92097 21.9049 6.48409C23.468 8.04721 24.5325 10.0387 24.9638 12.2069C25.395 14.375 25.1737 16.6223 24.3277 18.6646C23.4818 20.7069 22.0492 22.4525 20.2112 23.6806C18.3731 24.9088 16.2122 25.5643 14.0016 25.5643C11.0383 25.561 8.19735 24.3824 6.10198 22.287C4.00662 20.1916 2.828 17.3507 2.82471 14.3874Z"
                            fill="#0F0F74"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="h-full flex flex-col justify-between mt-10 mx-4 mb-6 gap-3">
                    {isDialPadCallVisible && (
                      <>
                        <PanelEditContact
                          lead={formObjContact}
                          action={handleOpenEditContactModal}
                        />
                        <DialPadCall
                          phoneNumbers={formObjContact.phoneNumbers}
                          phoneNumber={dialPadPhoneNumber}
                          onChangePhoneNumber={setDialPadPhoneNumber}
                        />
                      </>
                    )}
                    {!clicked && !isDialPadCallVisible && (
                      <LeadDetails
                        topBar={topBar}
                        contactExpanded={contactExpanded}
                        openEditContactModal={handleOpenEditContactModal}
                        lead={formObjContact}
                        isButtonEditContactDisabled={!leadLoaded}
                        handleContactExpanded={setContactExpanded}
                      />
                    )}
                    {clicked && (
                      <div className="rounded-[20px] bg-white/50 h-[474px] flex flex-col items-center justify-center">
                        <CallProgress
                          lead={formObjContact}
                          phoneNumberToCall={phoneNumberToCall}
                          dialPadPhoneNumber={dialPadPhoneNumber}
                          callStatus={callStatus}
                          timer={timer}
                          isDialPadPromptsVisible={isDialPadPromptsVisible}
                        />
                        {isDialPadPromptsVisible && (
                          <DialPadPrompts action={handleSendDigits} />
                        )}
                      </div>
                    )}
                    <div className="bg-white/50 rounded-[20px]">
                      {!clicked && !isDialPadCallVisible && (
                        <PhoneNumberSelector
                          phoneNumbers={formObjContact.phoneNumbers}
                          phoneNumberToCall={phoneNumberToCall}
                          setPhoneNumberToCall={setPhoneNumberToCall}
                        />
                      )}
                      <div className="grid grid-cols-5 items-center px-6 py-5">
                        {clicked ? (
                          <IconButton
                            buttonProps={{
                              className: 'w-12 h-12 justify-self-start',
                              onClick: handleOpenEditContactModal,
                            }}
                            iconProps={{
                              name: 'edit',
                              alt: 'edit',
                              className: 'h-8',
                            }}
                          />
                        ) : (
                          <IconButton
                            buttonProps={{
                              className: 'w-12 h-12 justify-self-start',
                              onClick: () => setOpenDeclineModal(true),
                              disabled: !leadLoaded,
                            }}
                            iconProps={{
                              name: 'trash',
                              alt: 'trash',
                              className: 'h-8',
                            }}
                          />
                        )}
                        <IconButton
                          buttonProps={{
                            className: twMerge(
                              'w-12 h-12 justify-self-start',
                              isMicroMute &&
                                '!bg-brand-900 hover:!bg-brand-900 focus:!bg-brand-900',
                              !clicked && 'invisible',
                            ),
                            onClick: handleMute,
                          }}
                          iconProps={{
                            name: isMicroMute ? 'micro-mute' : 'micro',
                            alt: 'micro',
                            className: 'h-8',
                          }}
                        />
                        <Button
                          title={dialButtonTitle}
                          onClick={handleDialClick}
                          disabled={isCallNotAvailable}
                          variant={clicked ? 'error' : 'secondary'}
                          iconStart={
                            loader ? (
                              <LoaderIcon className="h-5 w-5 animate-spin text-white" />
                            ) : (
                              <Icon
                                name="phone"
                                alt="phone"
                                className={twMerge(
                                  'h-6',
                                  clicked && 'rotate-[135deg]',
                                )}
                              />
                            )
                          }
                          className="w-[110px] justify-self-center"
                        />
                        <IconButton
                          buttonProps={{
                            className: twMerge(
                              'w-12 h-12 justify-self-end',
                              ((!!call && isDialPadPromptsVisible) ||
                                isDialPadCallVisible) &&
                                '!bg-brand-900 hover:!bg-brand-900 focus:!bg-brand-900',
                            ),
                            onClick: () => {
                              if (call) {
                                setIsDialPadPromptsVisible(prev => !prev);
                                return;
                              }
                              setIsDialPadCallVisible(prev => !prev);
                              setDialPadPhoneNumber('');
                            },
                            disabled:
                              !!call && callStatus !== CALL_STATUS.ACCEPT,
                          }}
                          iconProps={{
                            name:
                              (!!call && isDialPadPromptsVisible) ||
                              isDialPadCallVisible
                                ? 'dial-pad-active'
                                : 'dial-pad',
                            alt: 'dial-pad',
                            className: 'h-8',
                          }}
                        />
                        <ButtonCallNotes
                          onClick={handleOpenCallNotesModal}
                          disabled={!leadLoaded}
                          hasBg={!!formCallNotes}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <LeadsUnavailable />
              )
            }
          />
          {openEditContactModal && (
            <EditContactModal
              open={openEditContactModal}
              onClose={handleCloseEditContactModal}
              contact={formObjContact}
              onChangeContact={setFormObjContact}
            />
          )}
          {isSearchModalOpened && (
            <SearchModal
              open={isSearchModalOpened}
              onClose={handleCloseSearchModal}
              setSearchResults={setSearchResults}
              searchResults={searchResults}
              onFinish={handleFinish}
              setIsCallOptionsLoading={setIsCallOptionsLoading}
            />
          )}
          {openCallNotesModal && (
            <CallNotesModal
              open={openCallNotesModal}
              formObjNotes={formObjNotes}
              onClose={handleCloseCallNotesModal}
              onChangeFormObjNotes={setFormObjNotes}
              onChangeFormCallNotes={setFormCallNotes}
            />
          )}
          <DiscardModal
            isOpen={openDeclineModal}
            onClose={() => setOpenDeclineModal(false)}
            updateDialOutcomes={updateDialOutcomes}
            setHappenedIndex={setHappenedIndex}
            isDialOutcomesLoading={isDialOutcomesLoading}
            isDialOutcomesError={isDialOutcomesError}
          />
          {entryMode === 'Pitch' &&
            scriptSelected &&
            isOptimizeScriptWithAIModalOpened && (
              <OptimizeScriptWithAIModal
                open={isOptimizeScriptWithAIModalOpened}
                onClose={closeOptimizeScriptWithAIModal}
                setTabIndex={setTabIndex}
              />
            )}
          {isScriptModalOpened && (
            <ScriptModal
              open={isScriptModalOpened}
              onClose={handleCloseScriptEditModal}
              scriptSelected={scriptSelected}
              scriptEdit={scriptEdit}
              setScriptSelected={setScriptSelected}
              setTriggerCallerScipts={setTriggerCallerScipts}
              openOptimizeScriptWithAIModal={openOptimizeScriptWithAIModal}
              setScriptEdit={setScriptEdit}
            />
          )}
          {isUnsavedNumberModalOpened && (
            <UnsavedNumberModal
              open={isUnsavedNumberModalOpened}
              onClose={handleCloseUnsavedNumberModal}
              phoneNumber={dialPadPhoneNumber}
              onChangeIndex={setIndex}
              onChangeContact={setFormObjContact}
              contact={formObjContact}
              onChangePhoneNumber={setDialPadPhoneNumber}
            />
          )}
          {isFinishCallModalOpened && (
            <FinishCallModal
              open={isFinishCallModalOpened}
              contact={formObjContact}
              phoneNumber={dialPadPhoneNumber}
              onChangeContact={setFormObjContact}
              updateDialOutcomes={updateDialOutcomes}
              onClose={handleCloseFinishCallModal}
            />
          )}
        </div>
        {index === 5 && happenedIndex === 0 && (
          <AppointmentInformation
            timeInfo={timeInfo}
            pickerInfo={pickerInfo}
            appointment={appointment}
            formCallNotes={formCallNotes}
            lead={formObjContact}
            isDialOutcomesLoading={isDialOutcomesLoading}
            setIndex={setIndex}
            getUserTimeZone={getUserTimeZone}
            handleFinishCall={handleFinishCall}
            handleOpenCallNotesModal={handleOpenCallNotesModal}
            handleOpenEditContactModal={handleOpenEditContactModal}
          />
        )}
        <div
          className={`${
            (index === 5 && happenedIndex === 0) || index === 0
              ? 'hidden'
              : 'flex flex-col rounded-2xl bg-secondary/10 p-3 2xl:p-10'
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2 border-b-[3px] border-secondary/20 pb-2">
            <div className="relative flex items-center gap-2 before:absolute before:-bottom-[11px] before:left-0 before:h-[3px] before:w-[calc(100%+.5rem)] before:bg-secondary">
              <CallIcon className="w-5 h-5" />
              <h6 className="text-lg font-medium">Call Center</h6>
            </div>
          </div>
          <div className="mt-10 mx-4 mb-6 flex h-full flex-col justify-between gap-3">
            {isPanelEditContactVisible && (
              <PanelEditContact
                lead={formObjContact}
                action={handleOpenEditContactModal}
              />
            )}
            {pitchState.visible && (
              <Pitch
                pitchState={pitchState}
                callNotes={formCallNotes}
                onChangePitchState={setPitchState}
                handleOpenCallNotesModal={handleOpenCallNotesModal}
                onChangeIndex={setIndex}
                resetPitchState={resetPitchState}
              />
            )}
            {index === 1 && !pitchState.visible && (
              <WhatHappened
                setIndex={setIndex}
                setHappenedIndex={setHappenedIndex}
                happenedIndex={happenedIndex}
                handleFinishCall={handleFinishCall}
                handleOpen3={handleOpenCallNotesModal}
                formCallNotes={formCallNotes}
                resetPitchState={resetPitchState}
                isDialOutcomesLoading={isDialOutcomesLoading}
                hasPhoneNumberToSave={hasPhoneNumberToSave}
                handleOpenUnsavedNumberModal={handleOpenUnsavedNumberModal}
              />
            )}
            {index === 2 &&
              !pitchState.visible &&
              (happenedIndex === 0 || happenedIndex === 1) && (
                <Schedule1
                  onChangeSelectedDate={setPickerInfo}
                  setIndex={setIndex}
                  selectedDate={pickerInfo}
                  formObjContact={formObjContact}
                  onChangeFormObjContact={setFormObjContact}
                  handleOpen3={handleOpenCallNotesModal}
                  formCallNotes={formCallNotes}
                  resetPitchState={resetPitchState}
                />
              )}
            {index === 3 &&
              !pitchState.visible &&
              (happenedIndex === 0 || happenedIndex === 1) && (
                <Schedule2
                  happenedIndex={happenedIndex}
                  setIndex={setIndex}
                  onChangeSelectedTime={setTimeInfo}
                  selectedTime={timeInfo}
                  selectedDate={pickerInfo}
                  setFormObjContact={setFormObjContact}
                  formObjContact={formObjContact}
                  handleOpen3={handleOpenCallNotesModal}
                  formCallNotes={formCallNotes}
                />
              )}
            {index === 4 &&
              !pitchState.visible &&
              (happenedIndex === 0 || happenedIndex === 1) && (
                <Schedule3
                  appointment={appointment}
                  happenedIndex={happenedIndex}
                  setIndex={setIndex}
                  toggleIsPlayer={toggleIsPlayer}
                  pickerInfo={pickerInfo}
                  timeInfo={timeInfo}
                  handleFinishCall={handleFinishCall}
                  handleOpen3={handleOpenCallNotesModal}
                  formCallNotes={formCallNotes}
                  isDialOutcomesLoading={isDialOutcomesLoading}
                />
              )}
            {index === 7 && !pitchState.visible && happenedIndex === 2 && (
              <Email2
                formObjContact={formObjContact}
                handleFinishCall={handleFinishCall}
                handleOpen3={handleOpenCallNotesModal}
                formCallNotes={formCallNotes}
                resetPitchState={resetPitchState}
                isDialOutcomesLoading={isDialOutcomesLoading}
              />
            )}
            {index === 16 && !pitchState.visible && happenedIndex === 5 && (
              <NotInterested
                handleFinishCall={handleFinishCall}
                handleOpen3={handleOpenCallNotesModal}
                dialOutcomeDetail={dialOutcomeDetail}
                formCallNotes={formCallNotes}
                resetPitchState={resetPitchState}
                setDialOutcomeDetail={setDialOutcomeDetail}
                isDialOutcomesLoading={isDialOutcomesLoading}
              />
            )}
            {index === 18 && !pitchState.visible && happenedIndex === 8 && (
              <CommonDiscard
                handleFinishCall={handleFinishCall}
                dialOutcomeDetail={dialOutcomeDetail}
                setDialOutcomeDetail={setDialOutcomeDetail}
                handleOpen3={handleOpenCallNotesModal}
                formCallNotes={formCallNotes}
                resetPitchState={resetPitchState}
                isDialOutcomesLoading={isDialOutcomesLoading}
              />
            )}
            {index === 19 && !pitchState.visible && happenedIndex === 0 && (
              <Appointment
                appointment={appointment}
                setIndex={setIndex}
                setAppointment={setAppointment}
                handleOpen3={handleOpenCallNotesModal}
                formCallNotes={formCallNotes}
              />
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};
